import { ComponentType, FC, ReactElement } from 'react';
import { RouteObject } from 'react-router';
import { BoxProps } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { FeatureKey } from '~/core/shared/constants';
import {
  AnyDocument,
  Document,
  Option,
  TenantCustomComponentContent,
  TenantFeatureRequirement,
  TenantGuardRequirement,
  TenantIconsMap,
  TenantPermissionRequirement,
} from '~/core/shared/types';

export type TenantDocumentDefinition = string;
export type TenantDocumentObjectDefinition = AnyDocument;

export type TenantDocumentRouteProps = {
  tenantDocumentDefinition: string;
  tenantDocumentKey: string;
  tenantViewName: string;
  tenantNavigationItem: string;
  tenantTabName?: string;
};

export type Formlinks = {
  title: string;
  urlParam: string;
  page: string;
};

type TenantNavigationItemMeta = {
  text: string;
} & TenantPermissionRequirement &
  TenantFeatureRequirement;

export enum TenantNavigationItemType {
  Home = 'home',
  Href = 'href',
  Deal = 'deal',
}

type TenantNavigationItemHome = {
  type: TenantNavigationItemType.Home;
  href: string;
} & TenantNavigationItemMeta;

type TenantNavigationItemHref = {
  type: TenantNavigationItemType.Href;
  href: string;
} & TenantNavigationItemMeta;

type TenantNavigationItemDeal = {
  type: TenantNavigationItemType.Deal;
  href: string;
  definition: string;
} & TenantNavigationItemMeta;

export type TenantNavigationItems =
  | TenantNavigationItemHome
  | TenantNavigationItemHref
  | TenantNavigationItemDeal;

/**
 * `permission` is evaluated with `meetsRequirement` from `useCan`: a single key
 * matches with wildcard expansion and an array requires every key, the same
 * semantics as the header navigation and route-level gating.
 */
export type TenantSettingsNavigationItem = {
  href: string;
  text: string;
} & TenantPermissionRequirement &
  TenantFeatureRequirement;

export type TenantDocumentViewRoute = {
  path: string;
  props?: Record<string, any>;
} & TenantPermissionRequirement &
  TenantFeatureRequirement;

type TenantDocumentSubRoute = {
  path: string;
  component: ComponentType<any>;
  props?: Record<string, any>;
};

type TenantDocumentViewMeta = {
  route?: TenantDocumentViewRoute;
  documentRouteProps?: TenantDocumentRouteProps;
  type: string;
  nav?: string;
  routes?: TenantDocumentSubRoute[];
};

type TenantDocumentViewBuilderTemplate = {
  type: 'builderTemplate';
  /**
   * @TODO
   * Future feature: page/view builder based off of config like this:
   * https://weareflip.atlassian.net/wiki/spaces/INC/pages/2222817283/Configuration?focusedCommentId=2226683974#comment-2226683974
   */
} & TenantDocumentViewMeta;

export type TenantDocumentViewCustomComponent = {
  type: 'customComponent';
} & TenantCustomComponentContent &
  TenantDocumentViewMeta;

type TenantDocumentListViewFilterType = {
  type: 'form' | 'builder' | 'custom';
};

/**
 * @TODO like form builder in user journey
 */
type FormField = unknown;

type TenantDocumentListviewFilterForm = {
  form: string;
} & TenantDocumentListViewFilterType;

type TenantDocumentListViewFilterFromBuilder = {
  fields: FormField[];
} & TenantDocumentListViewFilterType;

type TenantDocumentListViewFilterCustomComponent =
  TenantCustomComponentContent & TenantDocumentListViewFilterType;

export type TenantDocumentListViewFilter =
  | TenantDocumentListviewFilterForm
  | TenantDocumentListViewFilterFromBuilder
  | TenantDocumentListViewFilterCustomComponent;

export type TenantDocumentListView<D extends Document> = {
  type: 'documentList';
  columns: ColumnDef<D>[];
  rowPath?: string;
  filter?: TenantDocumentListViewFilter;
  report?: string;
  customFilters?: ReactElement[];
} & TenantDocumentViewMeta;

type TenantDocumentDetailsViewMeta = {
  displayName?: (data: Record<string, any>) => string;
  includes?: string[];
};

export type TenantDocumentDetailsViewAction = {
  text?: string;
  key: string;
  contentComponent?: TenantCustomComponentContent;
  menuItemComponent?: TenantCustomComponentContent;
  context?: () => Record<string, any>;
} & TenantFeatureRequirement &
  TenantPermissionRequirement &
  TenantGuardRequirement;

type TenantDocumentDetailsTabRelation = {
  type: 'relation';
  definition: string;
  relation: string;
};

export type TenantDocumentDetailsTabComponent = {
  type: 'component';
} & TenantCustomComponentContent;

export type TenantDocumentDetailsViewTabContent =
  | TenantDocumentDetailsTabComponent
  | TenantDocumentDetailsTabRelation;

export type TenantDocumentDetailsViewTab = {
  key: string;
  route: TenantDocumentViewRoute;
  text: string;
  hidden?: boolean;
  type: 'component' | 'relation';
  feature?: FeatureKey;
  tabItem?: TenantCustomComponentContent;
  contentBoxProps?: BoxProps;
} & TenantDocumentDetailsViewTabContent;

export type TenantDocumentDetailsView = {
  type: 'documentDetails';
  meta?: TenantDocumentDetailsViewMeta;
  actions: TenantDocumentDetailsViewAction[];
  tabs: TenantDocumentDetailsViewTab[];
} & TenantDocumentViewMeta;

export type TenantDocumentView =
  | TenantDocumentViewBuilderTemplate
  | TenantDocumentListView<AnyDocument>
  | TenantDocumentDetailsView
  | TenantDocumentViewCustomComponent;

export type TenantDocumentDetailsViewContext = {
  config: TenantDocumentDetailsView;
};

export type FormFieldConfig = {
  name: string;
  label: string;
  component: string;
  suggestion?: boolean;
  variant?: string;
  isMulti?: boolean;
  clearable?: boolean;
  options?: Option[];
};

export type FormConfig = {
  fields: FormFieldConfig[];
};

export type TenantDocument<T extends TenantDocumentDefinition> = {
  key: string;
  definition: T;
  views: Record<string, TenantDocumentView>;
  formLinks?: Formlinks[];
  formConfig?: FormConfig;
  icons?: TenantIconsMap;
};

export type SummaryTabBlockConfig = {
  variant?: string;
  view?: TenantCustomComponentContent;
  multipleDocumentsContext?: string;
};

export type DocumentManagementDefinition = {
  definition: string;
  definitionName: string;
  plural: string;
  singular: string;
  route: string;
  viewPermission: string;
  updatePermission: string;
  views: {
    index: TenantDocumentListView<TenantDocumentObjectDefinition>;
    form: FC;
  };
  typeOptions?: string[];
};

export enum DocumentManagementType {
  DataList = 'data-list',
}

export type DocumentManagement = {
  for: DocumentManagementType;
  definitions: DocumentManagementDefinition[];
};
